// Arranque de analytics (EC-3811).
//
// Se importa por su efecto secundario desde src/index.js, y además se reintenta
// en cada cambio de ruta desde AnalyticsSessionBridge — ver ensureAnalyticsStarted.
import { analytics } from "./core";
import { createConsoleAdapter } from "./adapters/console.adapter";
import { createCompositeAdapter } from "./adapters/composite.adapter";
import { createLazyAdapter } from "./adapters/lazy.adapter";
import { noopAdapter } from "./adapters/noop.adapter";
import {
  analyticsDisabled,
  analyticsHost,
  analyticsKey,
  appVersion,
  environment,
  isDev,
} from "./env";
import type { AnalyticsAdapter } from "./types";
import type { Redaction } from "./sanitize";

/**
 * Rutas donde el SDK no se carga, por ninguna razón: llevan credenciales en la URL.
 *
 *   /sso-bridge#token=<JWT>&user=<base64url con email, nombre, apellido, rol>
 *   /reset-password?token=<token de recupero>
 *
 * La alternativa de "sanear la URL antes de que el SDK la lea" no sirve y además
 * es peligrosa: SsoBridge lee el hash en un useMemo (SsoBridge.tsx:59) y recién lo
 * limpia en la línea 103, y utilities/axios.interceptors.ts tiene otro fallback
 * que también lo lee. Adelantar la limpieza rompe el login SSO.
 *
 * No cargar el SDK es estrictamente más seguro: no hay URL que sanear, no hay
 * $initial_current_url que persistir y no hay carrera con el useEffect.
 */
const CREDENTIAL_BEARING_PATHS = ["/sso-bridge", "/reset-password"];

/** Marca que la sesión entró por el bridge de SSO (login-as-user desde el BO). */
export const IMPERSONATION_FLAG = "ec_analytics_impersonating";

function isCredentialBearingPath(): boolean {
  if (typeof window === "undefined") return false;
  return CREDENTIAL_BEARING_PATHS.some((prefix) =>
    window.location.pathname.startsWith(prefix)
  );
}

function readSessionFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    // Modo incógnito o storage bloqueado.
    return false;
  }
}

/** Permite auditar en uat/prod: localStorage.ec_analytics_debug = "1". */
function wantsConsoleMirror(): boolean {
  try {
    return window.localStorage.getItem("ec_analytics_debug") === "1";
  } catch {
    return false;
  }
}

function selectAdapter(): AnalyticsAdapter {
  if (analyticsDisabled) return noopAdapter;
  if (isDev) return createConsoleAdapter();
  if (!analyticsKey) return noopAdapter;

  // Import dinámico: posthog-js queda en su propio chunk y sólo se descarga acá,
  // o sea nunca en desarrollo, nunca sin key y nunca con el kill switch puesto.
  const posthog = createLazyAdapter("posthog(lazy)", () =>
    import("./adapters/posthog.adapter").then((module) =>
      module.createPosthogAdapter({
        apiKey: analyticsKey,
        apiHost: analyticsHost,
        debug: false,
      })
    )
  );

  return wantsConsoleMirror()
    ? createCompositeAdapter([posthog, createConsoleAdapter()])
    : posthog;
}

function onRedaction(redaction: Redaction): void {
  if (!isDev) {
    // En producción no se loguea: el contador ya viaja en el evento como
    // _redaction_count, y sobre eso hay una alerta permanente en PostHog.
    return;
  }
  // En desarrollo, ruidoso y con stack para llegar al call site culpable.
  // No se lanza: romper la app por telemetría es peor que el bug que reporta.
  // eslint-disable-next-line no-console
  console.error(
    `[analytics] PII BLOQUEADA en "${redaction.event ?? "?"}": prop "${redaction.key}" (${redaction.rule}). Corregir el call site.`,
    new Error().stack
  );
}

let started = false;

/**
 * Inicializa analytics una sola vez, y NO lo hace mientras la ruta actual lleve
 * credenciales en la URL.
 *
 * Es idempotente y se reintenta en cada navegación (AnalyticsSessionBridge). Ese
 * reintento es lo que evita que una sesión que entró por /sso-bridge quede sin
 * medir para siempre: el SDK no se carga en el bridge, pero sí en cuanto la app
 * navega al dashboard.
 */
export function ensureAnalyticsStarted(): void {
  if (started) return;
  if (isCredentialBearingPath()) return;

  try {
    analytics.init(
      selectAdapter(),
      {
        portal: "backoffice",
        environment,
        app_version: appVersion,
        is_impersonating: readSessionFlag(IMPERSONATION_FLAG),
      },
      { onRedaction }
    );
    started = true;
  } catch {
    // Analytics nunca puede tumbar la app: src/index.js es el entry point, un
    // throw acá sería pantalla blanca y el repo no tiene Error Boundary.
    started = true;
  }
}

ensureAnalyticsStarted();
